import JSZip from 'jszip';
import { CharacterPortrait } from '../../types';
import { AllowedEmotion, ALLOWED_EMOTIONS, normalizeEmotionsRecord } from './emotion-presets';

const IMAGE_EXT = /\.(png|jpe?g|gif|webp)$/i;

/** Tokens DarwinX (suffixe fichier avant _Vn) → clé émotion du moteur. */
const EMOTION_TOKEN_MAP: ReadonlyArray<{ token: string; emotion: AllowedEmotion }> = [
  { token: 'LAUGHING_OUT_LOUD', emotion: 'laughing' },
  { token: 'NEUTRAL', emotion: 'neutre' },
  { token: 'HAPPY', emotion: 'happy' },
  { token: 'ANGRY', emotion: 'angry' },
  { token: 'SURPRISED', emotion: 'surprised' },
  { token: 'SAD', emotion: 'sad' },
  { token: 'SUSPICIOUS', emotion: 'suspicious' },
  { token: 'WORRIED', emotion: 'worried' },
  { token: 'WAVING', emotion: 'waving' },
];

export interface ParsedCharacterZipEntry {
  /** Chemin dans le zip (ex. asset_char_boris/CHAR_….png) */
  zipPath: string;
  filename: string;
  emotion: AllowedEmotion;
  blob: Blob;
}

export interface ParsedCharacterZip {
  folderName: string;
  characterId: string;
  characterName: string;
  entries: ParsedCharacterZipEntry[];
}

/** Extrait le nom affiché depuis un dossier asset_char_*. */
export function characterNameFromAssetFolder(folderName: string): string {
  const stripped = folderName.replace(/^asset_char_/i, '');
  const raw = stripped
    .replace(/_-_/g, ' - ')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return raw.replace(/(^|[\s-])(\p{L})/gu, (_, sep, letter) => sep + letter.toUpperCase());
}

/** Slug stable pour id personnage et sous-dossier assets/characters/. */
export function characterIdFromAssetFolder(folderName: string): string {
  const stripped = folderName.replace(/^asset_char_/i, '');
  const slug = stripped
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug || 'character';
}

/** Parse l'émotion depuis un nom CHAR_*_EMOTION_V1.png */
export function parseEmotionFromFilename(filename: string): AllowedEmotion | null {
  const base = filename.replace(/\.[^.]+$/i, '');
  const withoutVersion = base.replace(/_V\d+$/i, '');
  const upper = withoutVersion.toUpperCase();

  for (const { token, emotion } of EMOTION_TOKEN_MAP) {
    if (upper.endsWith(`_${token}`)) return emotion;
  }
  return null;
}

function folderFromZipPath(zipPath: string): string {
  const parts = zipPath.replace(/\\/g, '/').split('/').filter(Boolean);
  if (parts.length < 2) return '';
  return parts[parts.length - 2];
}

/** Déduit le dossier asset_char_* depuis un nom de fichier CHAR_* seul (zip à plat). */
function folderHintFromCharFilename(filename: string): string {
  const base = filename.replace(/\.[^.]+$/i, '').replace(/_V\d+$/i, '');
  const upper = base.toUpperCase();
  for (const { token } of EMOTION_TOKEN_MAP) {
    if (upper.endsWith(`_${token}`)) {
      const prefix = base.slice(0, -(token.length + 1));
      if (/^CHAR_/i.test(prefix)) {
        return `asset_char_${prefix.slice(5).toLowerCase()}`;
      }
    }
  }
  return 'asset_char_character';
}

function isMacOsMetadata(path: string): boolean {
  const normalized = path.replace(/\\/g, '/');
  return normalized.includes('__MACOSX') || normalized.split('/').pop()?.startsWith('._') === true;
}

/**
 * Décompresse un zip export DarwinX (dossier asset_char_* avec sprites _Vx).
 */
export async function parseCharacterZip(file: File): Promise<ParsedCharacterZip> {
  const zip = await JSZip.loadAsync(file);
  const imageEntries: Array<{ zipPath: string; filename: string; folder: string; blob: Blob }> = [];

  for (const [zipPath, entry] of Object.entries(zip.files)) {
    if (entry.dir || isMacOsMetadata(zipPath)) continue;
    const filename = zipPath.replace(/\\/g, '/').split('/').pop() ?? '';
    if (!IMAGE_EXT.test(filename)) continue;
    if (!/^CHAR_/i.test(filename)) continue;

    const blob = await entry.async('blob');
    imageEntries.push({
      zipPath,
      filename,
      folder: folderFromZipPath(zipPath),
      blob,
    });
  }

  if (imageEntries.length === 0) {
    throw new Error(
      'Aucune image CHAR_* trouvée dans le zip. Attendu : dossier asset_char_* avec des sprites emotion_V1.png.'
    );
  }

  const folderCounts = new Map<string, number>();
  for (const item of imageEntries) {
    const folder = item.folder || folderHintFromCharFilename(item.filename);
    folderCounts.set(folder, (folderCounts.get(folder) ?? 0) + 1);
  }
  const folderName =
    [...folderCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ??
    folderHintFromCharFilename(imageEntries[0].filename);

  const entries: ParsedCharacterZipEntry[] = [];
  for (const item of imageEntries) {
    const itemFolder = item.folder || folderHintFromCharFilename(item.filename);
    if (itemFolder !== folderName) continue;
    const emotion = parseEmotionFromFilename(item.filename);
    if (!emotion) continue;
    entries.push({
      zipPath: item.zipPath,
      filename: item.filename,
      emotion,
      blob: item.blob,
    });
  }

  if (entries.length === 0) {
    throw new Error('Aucune émotion reconnue (NEUTRAL, HAPPY, ANGRY, …) dans les fichiers du zip.');
  }

  return {
    folderName,
    characterId: characterIdFromAssetFolder(folderName),
    characterName: characterNameFromAssetFolder(folderName),
    entries,
  };
}

/** Fusionne les sprites d'un zip dans un personnage existant (mise à jour + ajout d'émotions). */
export function mergeCharacterFromZipUpdate(
  existing: CharacterPortrait,
  uploadedPaths: Map<AllowedEmotion, string>
): Partial<CharacterPortrait> {
  const emotions = { ...normalizeEmotionsRecord(existing.emotions) };
  for (const [emotion, path] of uploadedPaths) {
    emotions[emotion] = path;
  }

  const patch: Partial<CharacterPortrait> = { emotions };

  const neutrePath = uploadedPaths.get('neutre');
  if (neutrePath) {
    patch.defaultImage = neutrePath;
    patch.icon = neutrePath;
  }

  return patch;
}

export function buildCharacterFromImport(
  parsed: ParsedCharacterZip,
  uploadedPaths: Map<AllowedEmotion, string>
): CharacterPortrait {
  const emotions: Record<string, string> = {};
  for (const entry of parsed.entries) {
    const path = uploadedPaths.get(entry.emotion);
    if (path) emotions[entry.emotion] = path;
  }

  const defaultImage = emotions.neutre ?? Object.values(emotions)[0] ?? '';

  return {
    id: parsed.characterId,
    name: parsed.characterName,
    defaultImage,
    icon: defaultImage,
    emotions,
  };
}
